import type { ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { formatInTimeZone } from 'date-fns-tz';
import type { FixingIndex, FixingIndexMarkMessageData } from '../../../types/FixingIndex';
import { createDateOnly, type DateOnly } from '../../../utils';
import { HStack } from '../../Core';
import { InlineFormattedNumber } from '../../FormattedNumber';
import { Text } from '../../Text';
import { baseColumn } from './baseColumn';
import type { ColDefFactory, Column } from './types';
import { numericColumnComparator } from './utils';

const INDEX_MARK_DATE_FORMAT = 'yyyy-MM-dd';
export interface IndexMarkColumnParams {
  /** If true, will pass the FixingIndex's MinFixingIncrement to the InlineFormattedNumber for rendering */
  useFixingIncrement?: boolean;
}

function formattedMarkDate(nominalTime: string | undefined, indexTimeZone: string | undefined): DateOnly | undefined {
  if (!nominalTime || !indexTimeZone) {
    return undefined;
  }

  try {
    return createDateOnly(formatInTimeZone(nominalTime, indexTimeZone, INDEX_MARK_DATE_FORMAT)) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Point this column's field to a `FixingIndexMarkMessageData` object and it will render:
 * - the mark value
 * - its nominal date in the fixing index timezone (`yyyy-MM-dd`)
 */
export const indexMark: ColDefFactory<Column<IndexMarkColumnParams>> = column => ({
  ...baseColumn(column),
  headerClass: 'ag-right-aligned-header',
  comparator: (valueA: FixingIndexMarkMessageData | undefined, valueB: FixingIndexMarkMessageData | undefined) =>
    numericColumnComparator(valueA?.Mark, valueB?.Mark),
  valueFormatter: ({ value }: ValueFormatterParams<unknown, FixingIndexMarkMessageData | undefined>) => {
    return value?.Mark ?? '';
  },
  cellRenderer: ({ value, context }: ICellRendererParams<unknown, FixingIndexMarkMessageData | undefined>) => {
    if (!value) {
      return null;
    }

    const fixingIndex: FixingIndex | undefined = context.current.fixingIndicesByID?.get(value.IndexID);
    const markDate = formattedMarkDate(value.NominalTime, fixingIndex?.NominalIndexTimeZone);

    return (
      <HStack w="100%" justifyContent="flex-end" gap="spacing4">
        <InlineFormattedNumber
          number={value.Mark}
          currency={fixingIndex?.QuoteCurrency}
          increment={column.params?.useFixingIncrement ? fixingIndex?.MinFixingIncrement : undefined}
        />
        {markDate && (
          <Text color="colorTextSubtle" fontStyle="italic">
            {markDate}
          </Text>
        )}
      </HStack>
    );
  },
});
